/**
 * Collect unique token icons and slugs
 */
import { useEffect, useRef, useState } from 'react';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import { KattanaAPI } from '../../store/createStore';
import { BASE_TOKEN, NativeCoin } from '../Trading/helpers/nativeCoins';
import { IconsMapping } from '../ConnectedWallet/NetworkBaseCoin';

type Item =
  | {
      chain: string;
      dest_address: string;
      src_address: string;
    }
  | {
      chain: string;
      src_token: string;
      dst_token: string;
    };

const createDictionary = (data: Item[]): string[] => {
  const dictionary = data.reduce<Record<string, number>>((acc, item) => {
    if ('src_token' in item && 'dst_token' in item) {
      const srcKey = `${item.chain}-${item.src_token}`;
      const destKey = `${item.chain}-${item.dst_token}`;
      acc[srcKey] = 1;
      acc[destKey] = 2;
    }
    if ('dest_address' in item && 'src_address' in item) {
      const srcKey = `${item.chain}-${item.src_address}`;
      const destKey = `${item.chain}-${item.dest_address}`;
      acc[srcKey] = 1;
      acc[destKey] = 2;
    }
    return acc;
  }, {});
  return Object.keys(dictionary);
};

type tokensMap = Record<
  string,
  { name: string; icon: string; decimals: number }
>;

const createMergeState = async (tickers: tokensMap, tickersKeys: string[]) => {
  const newState: tokensMap = {};
  for await (const key of tickersKeys) {
    if (!tickers[key]) {
      const [chain, ...address] = key.split('-');
      const tokenAddress = address.join('-');

      if (tokenAddress.toLowerCase() === BASE_TOKEN || !tokenAddress) {
        const nativeToken = NativeCoin(
          Network.fromChain(chain).toDecimal(),
          BigNumber(0)
        );
        newState[key] = {
          name: nativeToken.name,
          icon: IconsMapping[chain],
          decimals: nativeToken.decimals,
        };
      } else {
        const data = await KattanaAPI.tokenInfo(chain, tokenAddress, {});
        if (data) {
          const token = data.tokens?.find((token) => token.network === chain);
          newState[key] = {
            name: token?.symbol || data.slug,
            icon: data.logo_url,
            decimals: token?.decimals || 18,
          };
        }
      }
    }
  }
  return newState;
};

const useCollectTokens = (data: Item[]) => {
  const tokens = useRef<tokensMap>({});
  const [loadingTokens, setLoadingTokens] = useState<boolean>(false);

  // Collect tickers
  useEffect(() => {
    let flag = true;
    setLoadingTokens(true);

    if (data?.length) {
      const keys = createDictionary(data);
      createMergeState(tokens.current, keys).then((merge) => {
        if (flag) {
          tokens.current = {
            ...tokens.current,
            ...merge,
          };
          setLoadingTokens(false);
        }
      });
    } else {
      if (flag) {
        tokens.current = {};
        setLoadingTokens(false);
      }
    }

    return () => {
      flag = false;
    };
  }, [data]);

  return {
    tokens: tokens.current,
    loadingTokens,
  };
};

export default useCollectTokens;
